import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Grid, LinearProgress, Paper, Snackbar, Tab, Tabs, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useUserStore } from 'store';

import { flattenDrfError } from '../../utils/apiUtils';
import { isJobActive, websiteApi } from './websiteApi';
import SitePreview from './SitePreview';
import ContentEditor from './ContentEditor';
import PublishBar from './PublishBar';
import TrialBanner from './TrialBanner';
import RoiLine from './RoiLine';
import PublicationsDialog from './PublicationsDialog';
import ImagesPanel from './ImagesPanel';

const JOB_POLL_MS = 3000;
const TOKEN_REFRESH_MS = 40 * 60 * 1000;

/**
 * Nettisivut. The first thing the workshop sees is its own finished site (level 1 built in
 * the background); the panel next to it is the level 3 editor. Publishing starts the free
 * 30 days; the ROI line shows bookings that came through the site.
 */
export default function WebsitePage() {
  const { t } = useTranslation();
  const orgId = useUserStore((s) => s.currentOrganization?.organization_id);

  const [website, setWebsite] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [contentPayload, setContentPayload] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [device, setDevice] = useState('phone');
  const [tab, setTab] = useState(0);
  const [focusField, setFocusField] = useState('');
  const [publications, setPublications] = useState([]);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regeneratingField, setRegeneratingField] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const pollRef = useRef(null);

  const notify = useCallback((message, severity = 'success') => setSnackbar({ open: true, message, severity }), []);

  const loadPreview = useCallback(async () => {
    try {
      const { data } = await websiteApi.previewToken(orgId);
      setPreviewUrl(data.url);
      setReloadKey((k) => k + 1);
    } catch {
      setPreviewUrl('');
    }
  }, [orgId]);

  const loadContent = useCallback(async () => {
    const { data } = await websiteApi.content(orgId);
    setContentPayload(data.content ? data : null);
    return data;
  }, [orgId]);

  const loadAll = useCallback(async () => {
    try {
      const { data } = await websiteApi.config(orgId);
      setWebsite(data);
      setActiveJob(data.active_job);
      const content = await loadContent();
      if (content?.content) await loadPreview();
      try {
        const statsResponse = await websiteApi.stats(orgId);
        setStats(statsResponse.data);
      } catch {
        setStats(null);
      }
      setLoadError(false);
    } catch (err) {
      console.error('Failed to load website:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [orgId, loadContent, loadPreview]);

  useEffect(() => {
    if (orgId) loadAll();
  }, [orgId, loadAll]);

  // Poll the active generation job; when it finishes, refresh content and preview.
  useEffect(() => {
    if (!isJobActive(activeJob)) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await websiteApi.job(orgId, activeJob.id);
        if (!isJobActive(data)) {
          setActiveJob(null);
          setRegeneratingField('');
          if (data.status === 'done') {
            await loadContent();
            await loadPreview();
            const cfg = await websiteApi.config(orgId);
            setWebsite(cfg.data);
          } else {
            notify(t('website.generation_failed'), 'error');
          }
        }
      } catch {
        // keep polling
      }
    }, JOB_POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [activeJob, orgId, loadContent, loadPreview, notify, t]);

  useEffect(() => {
    if (!previewUrl) return undefined;
    const timer = setInterval(loadPreview, TOKEN_REFRESH_MS);
    return () => clearInterval(timer);
  }, [previewUrl, loadPreview]);

  const run = async (fn, successKey) => {
    setBusy(true);
    try {
      const result = await fn();
      if (successKey) notify(t(successKey));
      return result;
    } catch (err) {
      const code = err?.response?.data?.code;
      notify(code === 'daily_cap' ? t('website.daily_cap') : flattenDrfError(err, t('website.action_failed')), 'error');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async (body) => {
    setSaving(true);
    try {
      const { data } = await websiteApi.patchContent(orgId, body);
      setContentPayload(data);
      await loadPreview();
      notify(t('website.saved'));
    } catch (err) {
      notify(flattenDrfError(err, t('website.action_failed')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async (field) => {
    setRegeneratingField(field);
    const job = await run(() => websiteApi.regenerate(orgId, field).then((r) => r.data));
    if (job) setActiveJob(job); else setRegeneratingField('');
  };

  const handleGenerateAll = async () => {
    const job = await run(() => websiteApi.generate(orgId).then((r) => r.data));
    if (job) setActiveJob(job);
  };

  const handlePublish = async () => {
    const result = await run(() => websiteApi.publish(orgId).then((r) => r.data), 'website.published');
    if (result) setWebsite((w) => ({ ...w, ...result.website }));
  };

  const handleUnpublish = async () => {
    const result = await run(() => websiteApi.unpublish(orgId).then((r) => r.data), 'website.unpublished');
    if (result) setWebsite((w) => ({ ...w, ...result.website }));
  };

  const handleConfirm = async () => {
    const result = await run(() => websiteApi.confirm(orgId).then((r) => r.data), 'website.confirmed');
    if (result?.website) setWebsite((w) => ({ ...w, ...result.website }));
  };

  const openVersions = async () => {
    const rows = await run(() => websiteApi.publications(orgId).then((r) => r.data));
    if (rows) {
      setPublications(rows);
      setVersionsOpen(true);
    }
  };

  const handleRestore = async (publicationId) => {
    const result = await run(() => websiteApi.restore(orgId, publicationId).then((r) => r.data), 'website.restored');
    if (result) {
      setWebsite((w) => ({ ...w, ...result.website }));
      setVersionsOpen(false);
      await loadContent();
      await loadPreview();
    }
  };

  const handleFieldClick = useCallback((field) => {
    setTab(0);
    setFocusField(`${field}#${Date.now()}`);
  }, []);
  const focusPath = focusField.split('#')[0];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
    );
  }
  if (loadError) {
    return <Box sx={{ p: 3 }}><Alert severity="error">{t('website.load_error')}</Alert></Box>;
  }

  const generating = isJobActive(activeJob) && activeJob?.level === 1;
  const hasContent = Boolean(contentPayload?.content);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }} data-testid="website-page">
      <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>{t('website.title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('website.intro')}</Typography>

      <PublishBar
        website={website}
        hasContent={hasContent}
        busy={busy || generating}
        onPublish={handlePublish}
        onUnpublish={handleUnpublish}
        onShowVersions={openVersions}
        onGenerate={handleGenerateAll}
      />
      <TrialBanner website={website} onConfirm={handleConfirm} busy={busy} />
      <RoiLine stats={stats} />

      {generating && (
        <Box sx={{ mb: 2 }} data-testid="website-generating">
          <Typography variant="body2" sx={{ mb: 0.5 }}>{t('website.generating')}</Typography>
          <LinearProgress />
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <SitePreview
            previewUrl={previewUrl}
            editMode
            device={device}
            onDeviceChange={setDevice}
            onFieldClick={handleFieldClick}
            reloadKey={reloadKey}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2, maxHeight: 800, overflow: 'auto' }}>
            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }} variant="fullWidth">
              <Tab label={t('website.tab_texts')} data-testid="website-tab-texts" />
              <Tab label={t('website.tab_images')} data-testid="website-tab-images" />
            </Tabs>
            {tab === 0 && (hasContent ? (
              <ContentEditor
                content={contentPayload.content}
                fields={contentPayload.fields}
                sections={contentPayload.sections}
                focusField={focusPath}
                onSave={handleSave}
                onRegenerate={handleRegenerate}
                saving={saving}
                regeneratingField={regeneratingField}
                disabled={generating}
              />
            ) : (
              <Typography color="text.secondary">{t('website.no_content_yet')}</Typography>
            ))}
            {tab === 1 && <ImagesPanel orgId={orgId} content={contentPayload?.content} onChanged={async () => { await loadContent(); await loadPreview(); }} notify={notify} />}
          </Paper>
        </Grid>
      </Grid>

      <PublicationsDialog
        open={versionsOpen}
        onClose={() => setVersionsOpen(false)}
        publications={publications}
        onRestore={handleRestore}
        busy={busy}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
